import { useCallback, useMemo } from "react";
import { isNumber } from "lodash-es";
import { useMeasure } from "react-use";
import { Formatter as LegendFormatter } from "recharts/types/component/DefaultLegendContent";
import { Formatter as TooltipFormatter } from "recharts/types/component/DefaultTooltipContent";
import { useStatisticsNumberFormatter } from "./useStatisticsNumberFormatter";
import { ReportingMelcoMachineStatisticsModelsStatisticListItem } from "melco-shared-logic";
import { useTranslation } from "react-i18next";
//import { keyValue } from "../../../helper/reporting/reportingHelpers";

const BAR_GAP = 8;

const getBarSize = (amount: number) => {
  if (amount >= 1 && amount <= 15) return 30;
  if (amount >= 16 && amount <= 30) return 25;
  if (amount >= 31 && amount <= 50) return 15;
  return 10;
};

export const useStatisticsChart = (
  data: ReportingMelcoMachineStatisticsModelsStatisticListItem[]
) => {
  const { t } = useTranslation();

  const [wrapperRef, { width: wrapperWidth }] = useMeasure<HTMLDivElement>();

  const { formatNumber } = useStatisticsNumberFormatter();
  const barSize = useMemo(() => getBarSize(data.length), [data.length]);

  const tickLabelFormatter = (value: string) => {
    const maxLength = 12; //e.g "Maschine 999" would be displayed
    return value.length > maxLength ? `${value.slice(0, maxLength)}...` : value;
  };

  const tickNumberFormatter = useCallback(
    (num: number): string => {
      return formatNumber(num, true);
    },
    [formatNumber]
  );

  const toolTipNumberFormatter: TooltipFormatter<string, string> = useCallback(
    (value: string, name: string) => {
      return [isNumber(value) ? formatNumber(value) : value, keyValue(name)];
    },
    [formatNumber]
  );

  const legendFormatter: LegendFormatter = (value, entry) => {
    const { color } = entry;

    return <span style={{ color }}>{keyValue(value)}</span>;
  };

  const chartWidth = useMemo(() => {
    // width will decide whether scroll will come in Graph

    const width = data.length * (barSize + BAR_GAP);
    if (wrapperWidth > width) {
      return wrapperWidth;
    }
    return width;
  }, [wrapperWidth, data.length, barSize]);

  const keyMapping: { [key: string]: string } = {
    total_running_time: t("reporting.statistics.charts.total_running_time"),
    total_standby_time: t("reporting.statistics.charts.total_standby_time"),
    thread_breaks: t("reporting.statistics.charts.thread_breaks_tooltip"),
  };

  const keyValue = (key: string): string => {
    return keyMapping[key] || key;
  };

  return {
    tickLabelFormatter,
    tickNumberFormatter,
    toolTipNumberFormatter,
    legendFormatter,
    wrapperRef,
    chartWidth,
    maxBarSize: barSize,
  };
};
